/**
 * File picker wrapper (#36 — IOS-4.3).
 *
 * Opens the iOS document picker (UIDocumentPickerViewController) and returns
 * the selected file as a Blob + metadata. No-op (returns null) on web — the
 * web ImportPage falls back to a standard <input type="file"> element.
 *
 * On iOS without a Share Extension, this is the primary way for users to pick
 * a CSV or PDF from Files.app or another document provider and import it into
 * Pazuru. Share Extension (share TO the app from outside) is deferred to IOS-5.
 */

import { Capacitor } from '@capacitor/core'
import { FilePicker } from '@capawesome/capacitor-file-picker'

export interface PickedFile {
  name: string
  mimeType: string
  blob: Blob
}

const SUPPORTED_MIME_TYPES = [
  'text/csv',
  'text/plain',          // some CSV files come as text/plain
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

/**
 * Opens the platform file picker and returns the selected file.
 * Returns null if the user cancels or an error occurs.
 */
export async function pickImportFile(): Promise<PickedFile | null> {
  if (!Capacitor.isNativePlatform()) {
    // Web: caller should fall back to <input type="file">
    return null
  }
  try {
    const result = await FilePicker.pickFiles({
      types: SUPPORTED_MIME_TYPES,
      limit: 1,
      readData: true,
    })
    const file = result.files[0]
    if (!file || !file.data) return null

    // Plugin returns base64 data when readData: true
    const blob = base64ToBlob(file.data, file.mimeType ?? 'application/octet-stream')
    return {
      name: file.name,
      mimeType: file.mimeType ?? 'application/octet-stream',
      blob,
    }
  } catch {
    // User cancelled or picker dismissed — not an error
    return null
  }
}

function base64ToBlob(b64: string, mimeType: string): Blob {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mimeType })
}
